<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:link',
	name: '$t:blocks.input_list_m2m.name',
	description: '$t:blocks.input_list_m2m.description',
	category: 'input',
	tags: ['form-control'],
	options: [
		{ name: 'fieldId', label: '$t:blocks.common.props.field' },
		{ name: 'referencedItemKey', label: '$t:blocks.common.props.referenced_item' },
		{ name: 'disabled', label: '$t:blocks.common.props.disabled' },
		{ name: 'readonly', label: '$t:blocks.common.props.readonly' },
		{ name: 'disabledNotice', label: '$t:blocks.common.props.disabled_notice' },
	],

	hidden: ({ isRoot }) => isRoot,
}
</script>
<script lang="ts" setup>
import type { DefaultViewOptions } from '#layers/blocks/blocks/Listing.block.vue';
import type { UseM2MStateReturn } from '#layers/blocks/composables/relation/use-m2m-state';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { AnyItem, CompositePrimaryKey, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import type { FormSaveError, M2MEditState } from '#layers/forms/public';
import type { ButtonProps } from '#layers/ui/public';
import { useOverlay } from '@nuxt/ui/composables';
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { omit, toStructuredError } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import RelatedItemListCard from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import { useM2MDisplayItems } from '#layers/blocks/composables/relation/use-m2m-display-items';
import { useM2MRowActions } from '#layers/blocks/composables/relation/use-m2m-row-actions';
import { useM2MState } from '#layers/blocks/composables/relation/use-m2m-state';
import { getCollectionPrimaryKeyFields, isSameKey, pickByPrimitiveFields } from '#layers/data-model/public';
import { injectFormBlockContext, isCreatedItem, markCreatedItem, RelationEditKinds, useDrawerFieldOverrides, useM2MPermissions, useRelationFormField } from '#layers/forms/public';
import { LazyCollectionDetailDrawer, LazyCollectionListingDrawer } from '#layers/listing/lazy';

type M2MActionProps = Partial<Omit<ButtonProps, 'onClick'>> & { onClick?: (m2mApi: UseM2MStateReturn) => void };

export interface InputListM2MBlockProps extends Pick<InputBlockProps, 'disabled' | 'disabledNotice' | 'readonly'> {
	fieldId: GlobalSingleRelationFieldId;
	referencedItemKey: CompositePrimaryKey;
	defaultView?: DefaultViewOptions;
	createAction?: M2MActionProps;
	editShown?: boolean;
	/** Pluralizable i18n key used for the item-count header (e.g. `common.n_files`). */
	countLabel?: string;
}

const props = withDefaults(defineProps<InputListM2MBlockProps>(), {
	editShown: true,
});

;

const { t } = useI18n();

const listCard = useTemplateRef('listCard');

const relationFieldContext = useRelationFormField<'to-many'>(props);
const { field, disabled, disabledNotice } = relationFieldContext;

const pageSize = ref(25);

const m2mState = useM2MState({
	...relationFieldContext,
	pageSize,
});
const {
	items,
	targetCollection,
	m2mStructure,
	connectTarget,
	createTarget,
	undoCreateTarget,
	updateTarget,
	editConnectedTarget,
	captureTargetEdits,
	isNewTargetCreate,
	getTargetFromJunction,
	getJunctionKey,
	getTargetKeyFromJunction,
	getTargetReferenceKeyFromJunction,
	connectedTargetsFilter,
	pageNumber,
	totalItemsCount,
	currentItemsCount,
	unpagedItemsCount,
	isLoading,
	junctionPrimaryKeyFields,
	targetReferenceKeyFields,
} = m2mState;

const formBlockContext = injectFormBlockContext();
const permissions = useM2MPermissions({
	m2mStructure,
	writeContext: () => formBlockContext?.writeContext.value,
});

const fieldOverrides = useDrawerFieldOverrides(field, targetCollection);

const createActionProps = computed<Partial<ButtonProps>>(() => ({
	...omit(props.createAction ?? {}, ['onClick']),
}));

const { displayItems } = useM2MDisplayItems(m2mState);
const { removeJunction, undoJunction } = useM2MRowActions(m2mState);

function findJunctionByTarget(targetData: AnyItem): AnyItem | undefined {
	return items.value.find((item) => {
		const target = getTargetFromJunction(item);
		return target && isSameKey(target, targetData);
	});
}

async function handleSelect() {
	if (!targetCollection.value || !permissions.canConnect.value) return;

	const modal = useOverlay().create(LazyCollectionListingDrawer, {
		destroyOnClose: true,
		props: {
			collectionId: targetCollection.value.id,
			selectionMode: 'multiple',
			selectionKeys: targetReferenceKeyFields.value ?? undefined,
			projectionKeys: getCollectionPrimaryKeyFields(targetCollection.value),
			filter: connectedTargetsFilter.value,
			defaultView: props.defaultView,
		},
	});

	const result = await modal.open().result;

	if (result) {
		for (const target of result.selection) {
			connectTarget(pickByPrimitiveFields(target, targetReferenceKeyFields.value ?? []), {
				targetData: target,
			});
		}
	}
}

function handleRemove(targetData: AnyItem) {
	const junctionItem = findJunctionByTarget(targetData);
	if (junctionItem) removeJunction(junctionItem);
}

function handleUndo(targetData: AnyItem) {
	const junctionItem = findJunctionByTarget(targetData);
	if (junctionItem) undoJunction(junctionItem);
}

function editState(junctionItem: AnyItem): M2MEditState | undefined {
	if (isNewTargetCreate(junctionItem)) return { kind: RelationEditKinds.Created };
	const key = getTargetKeyFromJunction(junctionItem);
	if (!key) return;
	if (isCreatedItem(junctionItem)) return { kind: RelationEditKinds.Connected, key };
	const junctionKey = getJunctionKey(junctionItem);
	const referenceKey = getTargetReferenceKeyFromJunction(junctionItem);
	return junctionKey && referenceKey ? { kind: RelationEditKinds.Persisted, key, junctionKey, referenceKey } : undefined;
}

function canEditItem(targetData: AnyItem): boolean {
	const junction = findJunctionByTarget(targetData);
	const state = junction && editState(junction);
	return !!state && permissions.editContext(state) !== undefined;
}

async function handleEdit(targetData: AnyItem) {
	if (!targetCollection.value) return;

	const junctionItem = findJunctionByTarget(targetData);
	if (!junctionItem) return;

	const targetItem = getTargetFromJunction(junctionItem);
	const state = editState(junctionItem);
	const writeContext = state && permissions.editContext(state);
	if (!state || !writeContext) return;

	const modal = useOverlay().create(LazyCollectionDetailDrawer, {
		destroyOnClose: true,
		props: {
			collectionId: targetCollection.value.id,
			title: t('common.edit_item'),
			formMode: 'emit',
			writeContext,
			itemEdits: targetItem ?? undefined,
			itemId: state.kind === RelationEditKinds.Created ? undefined : state.key,
			fieldOverrides: fieldOverrides.value,
		},
	});

	const instance = modal.open();
	const payload = await instance.result;

	if (payload) {
		switch (state.kind) {
			case RelationEditKinds.Created:
				undoCreateTarget(junctionItem);
				createTarget(markCreatedItem(payload.data, junctionItem));
				break;
			case RelationEditKinds.Connected:
				editConnectedTarget(state.key, payload.data);
				break;
			case RelationEditKinds.Persisted:
				updateTarget({ ...state.key, ...state.referenceKey }, payload.data);
				break;
			default:
				state satisfies never;
		}
	}
}

async function handleCreate() {
	if (props.createAction?.onClick) {
		props.createAction.onClick(m2mState);
		return;
	}

	const writeContext = permissions.createContext.value;
	if (!targetCollection.value || !writeContext) return;

	const modal = useOverlay().create(LazyCollectionDetailDrawer, {
		destroyOnClose: true,
		props: {
			collectionId: targetCollection.value.id,
			title: t('common.create_item'),
			formMode: 'emit',
			writeContext,
			fieldOverrides: fieldOverrides.value,
		},
	});

	const instance = modal.open();
	const payload = await instance.result;

	if (payload) {
		createTarget(payload.data);
		listCard.value?.scrollToBottom();
	}
}

// Persist edits to newly connected/uploaded targets after the parent (and its junction
// _connect) has saved, so a cancelled form never mutates the shared target record.
formBlockContext?.onSave(() => {
	const saveTargetEdits = captureTargetEdits();

	return async (): Promise<FormSaveError[]> => {
		try {
			await saveTargetEdits();
			return [];
		}
		catch (error) {
			return [{ message: t('blocks.input_list_m2m.target_update_failed'), source: toStructuredError(error) }];
		}
	};
});
</script>

<template>
	<DisplayTemplateConfigurationError
		v-if="!targetCollection?.id"
		:error="$t('blocks.common.relation_configuration_error')"
	/>
	<DisplayTemplateConfigurationError
		v-else-if="junctionPrimaryKeyFields?.length === 0"
		:error="$t('blocks.common.junction_missing_primary_key')"
	/>
	<RelatedItemListCard
		v-else
		ref="listCard"
		:items="displayItems"
		:count-label
		:collection-id="targetCollection?.id"
		:state-key="fieldId"
		:disabled
		:disabled-notice
		:readonly
		:aria-readonly="readonly"
		:show-edit="editShown"
		:create-action-props
		:pagination="{
			page: pageNumber,
			pageSize,
			totalItems: totalItemsCount,
			currentItems: currentItemsCount,
			unpagedItems: unpagedItemsCount,
		}"
		:loading="isLoading"
		:permissions="{
			canConnect: permissions.canConnect.value,
			canCreate: permissions.canCreate.value,
			canEditItem,
			canDisconnect: permissions.canDisconnect.value,
		}"
		show-create
		show-add-existing
		@select="handleSelect"
		@create="handleCreate"
		@edit="handleEdit"
		@remove="handleRemove"
		@undo="handleUndo"
		@update:page="pageNumber = $event"
		@update:page-size="pageSize = $event"
	/>
</template>
